import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef
} from "@angular/material/dialog";
import { TicketEditDialogData } from "@app/view/planning/ticket-edit-dialog/ticket-edit-dialog-data";
import { Ticket, Ticket_DetailProjection } from "@app/models/rest/ticket";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import { ResourceAvailability_DetailProjection } from "@app/models/rest/resource-availability";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { Resource_DetailProjection } from "@app/models/rest/resource";
import { ResourceStore } from "@app/services/store/resource-store";
import { TicketStreamingStore } from "@app/services/streaming/ticket-streaming-store";
import moment, { Moment } from "moment";
import { ResourceChooserDialogComponent } from "@app/view/shared/resource-chooser-dialog/resource-chooser-dialog.component";
import { firstValueFrom, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { RuleType } from "@app/models/enums/resource-rule-type";
import { MatCheckbox } from "@angular/material/checkbox";
import { FormsModule } from "@angular/forms";
import { MatCardAvatar } from "@angular/material/card";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatProgressBar } from "@angular/material/progress-bar";
import { DialogHeaderComponent } from "@app/view/shared/dialog-header/dialog-header.component";

@Component({
  selector: "app-ticket-edit-dialog",
  templateUrl: "./ticket-edit-dialog.component.html",
  styleUrls: ["./ticket-edit-dialog.component.scss"],
  imports: [
    MatDialogContent,
    MatCheckbox,
    FormsModule,
    MatButton,
    MatIcon,
    MatInput,
    MatProgressBar,
    MatDialogActions,
    DialogHeaderComponent
  ]
})
export class TicketEditDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<TicketEditDialogComponent>>(MatDialogRef);
  ticketStore = inject(TicketStreamingStore);
  resourceStore = inject(ResourceStore);
  protected serviceErrorHandler = inject(ServiceErrorHandlerService);
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  dialog = inject(MatDialog);
  private clientConfigService = inject(ClientConfigService);
  private store = inject<Store<any>>(Store);
  data = inject<TicketEditDialogData>(MAT_DIALOG_DATA);

  loading: boolean = false;
  error: boolean = false;
  errorMessage: string;
  selectedResourceAvailability: ResourceAvailability_DetailProjection;
  resource: Resource_DetailProjection;
  lockResource: boolean = false;
  lockTime: boolean = false;
  hasSkills: boolean;
  isForbidden: boolean = false;
  withinAppointmentWindow: boolean = false;
  lockTimeValue: any;
  public allowResourceLocking: boolean = false;
  public invalidResource: boolean = false;
  public appointmentStartOnly: boolean = false;
  /**
   * True when the ticket's job is Orchestrator-managed (has an
   * externalProjectReference). Such tickets must not be moved or locked in
   * Resolve, so the dialog blocks confirmation outright. (#1529)
   */
  public isProject: boolean = false;
  private ticket: Ticket_DetailProjection;
  private timeValidationSubject = new Subject<string>();
  private unsubscribe$ = new Subject<void>();
  // Monotonic token so a slow, superseded recheckForm() can't clobber the
  // latest validation state. (#1511)
  private recheckSeq = 0;

  constructor() {
    const data = this.data;

    console.log("starting editor with data");
    console.log(data);
    this.lockTimeValue = moment(data.newForceStartTime).format("HH:mm");
    this.appointmentStartOnly =
      this.clientConfigService.config?.appointmentStartOnly ?? false;

    // Set up debounced validation for time changes
    this.timeValidationSubject
      .pipe(debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe((timeValue) => {
        this.validateTimeChange(timeValue);
      });

    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.allowResourceLocking = !!auth && auth.allowResourceLocking;
      if (!this.allowResourceLocking) {
        this.data.newResourceId = this.data.oldResourceId;
        this.recheckForm(true);
      }
    });
  }

  recheckForm(initial: boolean) {
    const seq = ++this.recheckSeq;
    // Load the ticket and resource up front (deduped — both branches needed the
    // ticket). All shared-state mutations (including the lockResource
    // initialisation) happen *after* the staleness guard below, so a superseded
    // recheck can never affect the current one. (#1511)
    Promise.all([
      this.resourceStore.loadSingleProjection<Resource_DetailProjection>(
        Resource_DetailProjection,
        this.data.newResourceId,
        "detail"
      ),
      this.ticketStore.loadSingleProjection<Ticket_DetailProjection>(
        Ticket_DetailProjection,
        this.data.ticketId,
        "detail"
      )
    ])
      .then(([resource, ticket]) => {
        // When the time is locked, validate availability at the forced start
        // time; otherwise the solver can place the visit anywhere in its
        // appointment window, so just require any availability overlapping that
        // window rather than the dropped instant. (#1511)
        const apptStart = ticket.visit?.appointmentStart;
        const apptEnd = ticket.visit?.appointmentEnd;
        const useWindow = !this.lockTime && !!apptStart && !!apptEnd;
        const from = useWindow
          ? moment(apptStart).toDate()
          : this.data.newForceStartTime;
        const to = useWindow
          ? moment(apptEnd).toDate()
          : this.data.newForceStartTime;
        return this.resourceAvailabilityStore
          .findByResourceAndTimeRange(this.data.newResourceId, from, to)
          .then((resAv) => {
            // Ignore a superseded recheck: the dialog fires recheckForm() from
            // the auth subscription, ngOnInit, both checkboxes and the debounced
            // time validation, and those async chains overlap. Without this a
            // slow earlier call could land last and flip invalidResource back
            // to a stale value. (#1511)
            if (seq !== this.recheckSeq) {
              return;
            }
            // lockResource initialises from the ticket on the first load; set it
            // here (after the guard, before the availability gate reads it) so a
            // stale chain can't flip it. (#1511)
            if (initial && ticket.lockToResource) {
              this.lockResource = true;
            }
            this.resource = resource;
            this.ticket = ticket;
            this.isProject = !!ticket.visit?.job?.externalProjectReference;
            const invalid =
              !resAv ||
              !resAv.resources ||
              (resAv.resources.length === 0 && this.lockResource);
            this.invalidResource = invalid;
            this.selectedResourceAvailability = invalid
              ? null
              : resAv.resources[0];
            this.calculateHasSkills(ticket, resource);
            this.checkForbiddenList(ticket, resource);
            this.calculateValidTime(
              ticket,
              moment(this.data.newForceStartTime),
              moment(this.data.newForceStartTime).add(
                ticket.duration,
                "minutes"
              )
            );
          });
      })
      .catch((reason) => {
        // Only surface errors from the current recheck; stale failures are noise.
        if (seq === this.recheckSeq) {
          this.serviceErrorHandler.handleError(reason);
        }
      });
  }

  ngOnInit() {
    this.recheckForm(true);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onConfirmClick() {
    this.loading = true;

    this.ticketStore
      .loadSingle(this.data.ticketId)
      .then((storeTicket) => {
        if (this.lockTime) {
          let forceStartTime = moment(this.data.newForceStartTime);
          let timeComponents = this.lockTimeValue.split(":");
          forceStartTime.hour(timeComponents[0]);
          forceStartTime.minute(timeComponents[1]);

          storeTicket.forceStartTime = forceStartTime.toDate();
        } else {
          console.log("clearing forced start time");
          storeTicket.forceStartTime = null;
        }
        this.ticketStore.update(storeTicket).subscribe(
          (value: Ticket) => {
            console.log("ticket updated");

            if (this.lockResource) {
              console.log("updating resource lock");
              this.ticketStore
                .lockResourceAsync(
                  value.id,
                  this.selectedResourceAvailability.resource.id
                )
                .then((value1) => {
                  this.dialogRef.close({ updatedTicket: value1 });
                })
                .catch((reason) => {
                  // Close dialog and refresh ticket to show actual state
                  this.dialogRef.close({
                    updatedTicket: null,
                    refreshTicket: value.id
                  });
                  this.serviceErrorHandler.handleError(reason);
                });
            } else {
              firstValueFrom(storeTicket.getRelation("lockToResource"))
                .then((value1) => {
                  if (value1) {
                    console.log("clearing resource lock");
                    this.ticketStore
                      .lockResourceAsync(value.id, null)
                      .then((value1) => {
                        this.dialogRef.close({ updatedTicket: value1 });
                      })
                      .catch((reason) => {
                        // Close dialog and refresh ticket to show actual state
                        this.dialogRef.close({
                          updatedTicket: null,
                          refreshTicket: value.id
                        });
                        this.serviceErrorHandler.handleError(reason);
                      });
                  } else {
                    console.log("no change to resource lock");
                    this.dialogRef.close({ updatedTicket: value });
                  }
                })
                .catch((reason) => {
                  console.log("Error", reason);
                  if (reason && reason.status && reason.status === 404) {
                    console.log("Was no current lock to resource value");
                  }
                  this.dialogRef.close();
                });
            }
          },
          (reason) => {
            console.log("error, reason: " + reason);
            this.errorMessage = reason;
            this.error = true;

            this.serviceErrorHandler.handleError(reason);
          }
        );
      })
      .catch((reason) => {
        this.errorMessage = reason;
        this.error = true;
        this.serviceErrorHandler.handleError(reason);
      });
  }

  updateAppointmentWindowValidity($event: any) {
    console.log("updating appointment window validity", $event);
    // Use the subject to trigger debounced validation
    this.timeValidationSubject.next($event);
  }

  private validateTimeChange(timeValue: string) {
    console.log("validating time change", timeValue);

    // Always update the time and validate, regardless of lockTime checkbox
    // The checkbox only controls whether the time is applied when saving
    let forceStartTime = moment(this.data.newForceStartTime);

    // Ensure timeValue is properly formatted and valid
    if (timeValue && timeValue.includes(":")) {
      let timeComponents = timeValue.split(":");
      if (timeComponents.length >= 2) {
        const hours = parseInt(timeComponents[0], 10);
        const minutes = parseInt(timeComponents[1], 10);

        // Validate the time components
        if (
          !isNaN(hours) &&
          !isNaN(minutes) &&
          hours >= 0 &&
          hours <= 23 &&
          minutes >= 0 &&
          minutes <= 59
        ) {
          forceStartTime.hour(hours);
          forceStartTime.minute(minutes);
          this.data.newForceStartTime = forceStartTime.toDate();

          // Trigger form recheck to update all validations
          this.recheckForm(false);
        }
      }
    }
  }

  // Additional method to handle input events for more reliable change detection
  onTimeInputChange($event: any) {
    console.log("time input change event", $event);
    this.updateAppointmentWindowValidity($event.target.value);
  }

  changeResource() {
    const skills = [];
    this.ticket?.skillProfile?.skillRuleList?.forEach((skillRule) => {
      skills.push(skillRule.skill.id);
    });
    const dialogRef = this.dialog.open(ResourceChooserDialogComponent, {
      data: {
        requiredZoneId: this.ticket?.visit?.job?.zone?.id,
        requiredSkillIds: skills
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("The dialog was closed", result);
      this.data.newResourceId = result.data.id;
      this.recheckForm(false);
      // console.log(result);
      // this.resourceFormControl[ticket.id].setValue(result.data);
    });
  }

  private checkForbiddenList(
    ticket: Ticket_DetailProjection,
    newResource: Resource_DetailProjection
  ) {
    console.log("Checking forbidden list", ticket, newResource);
    let forbidden = false;
    if (!newResource.resourceRules) return;
    //if the resource has a rule that forbids the ticket.visit.job.externalPropertyReference then mark as forbidden
    if (
      newResource.resourceRules.find(
        (rule) =>
          rule.ruleType === RuleType.PROPERTY_EXCLUSION &&
          rule.ruleValue === ticket.visit.job.externalPropertyReference
      )
    ) {
      forbidden = true;
    }

    this.isForbidden = forbidden;
  }

  private calculateHasSkills(
    ticket: Ticket_DetailProjection,
    newResource: Resource_DetailProjection
  ) {
    console.log("calculating skills for ", ticket, newResource);
    let foundAllSkills = true;
    const skillRuleList = ticket?.skillProfile?.skillRuleList ?? [];
    for (
      let skillRuleIndex = 0;
      skillRuleIndex < skillRuleList.length;
      skillRuleIndex++
    ) {
      let skillRule = skillRuleList[skillRuleIndex];
      if (skillRule) {
        if (!newResource || !newResource.skillsList) {
          foundAllSkills = false;
          break;
        }
        let foundSkill = false;
        for (
          let skillIndex = 0;
          skillIndex < newResource.skillsList.length;
          skillIndex++
        ) {
          let skill = newResource.skillsList[skillIndex].skill;
          if (skill) {
            if (skillRule.skill.id === skill.id) {
              foundSkill = true;
              break;
            }
          }
        }
        if (!foundSkill) {
          foundAllSkills = false;
          break;
        }
      }
    }
    this.hasSkills = foundAllSkills;
  }

  private calculateValidTime(
    ticket: Ticket_DetailProjection,
    startDate: Moment,
    endDate: Moment
  ) {
    const appointmentStart = moment(ticket.visit.appointmentStart);
    const appointmentEnd = moment(ticket.visit.appointmentEnd);
    const startOnly =
      this.clientConfigService.config?.appointmentStartOnly ?? false;

    console.log(
      "validating",
      startDate.toDate(),
      endDate.toDate(),
      appointmentStart.toDate(),
      appointmentEnd.toDate(),
      "startOnly:",
      startOnly
    );

    const startMinute = startDate.startOf("minute");
    const isBefore = startMinute.isBefore(appointmentStart.startOf("minute"));

    if (startOnly) {
      // Only the start time needs to be within the appointment window
      const isAfter = startMinute.isAfter(appointmentEnd.startOf("minute"));
      this.withinAppointmentWindow = !isBefore && !isAfter;
    } else {
      // Both start and end must be within the appointment window
      const isAfter = endDate
        .startOf("minute")
        .isAfter(appointmentEnd.startOf("minute"));
      this.withinAppointmentWindow = !isBefore && !isAfter;
    }
  }
}
